import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { AisleCell, AisleResponse } from '../types'
import {
  ProductsCellRef,
  ViewMode,
  LEVEL_CHIP_W,
  MAP_MODE_LABELS,
  GRID_COL_HEADER_TEXT,
} from '../config/constants'
import { isAxisValue, numSort } from '../config/utils'
import { aisleDetailStyles as styles } from '../styles/AisleDetailModal.styles'
import { axisLabelSx, gridStyles } from '../styles/shared.styles'
import { getLayoutAisle } from '../api'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import { AisleMapCell } from './AisleMapCell'
import { CellSummaryPopper } from './CellSummaryPopper'
import { useCellSummary } from '../hooks/useCellSummary'
import { MapLegend } from './MapLegend'
import { ProductsModal } from './ProductsModal'

export function AisleDetailModal({
  open,
  aisleName,
  warehouseId,
  brandColors,
  zoneColors,
  highlightBrand = null,
  mode,
  onModeChange,
  onClose,
}: {
  open: boolean
  aisleName: string
  warehouseId: number
  brandColors: Map<string, string>
  zoneColors: Map<string, string>
  highlightBrand?: string | null
  mode: ViewMode
  onModeChange: (mode: ViewMode) => void
  onClose: () => void
}) {
  const { state, callApi } = useApi<AisleResponse>()
  const [productsCell, setProductsCell] = useState<ProductsCellRef | null>(null)
  const { containerRef, open: openCell, setOpen, close } = useCellSummary<HTMLDivElement>()

  useEffect(() => {
    if (open && aisleName && warehouseId) {
      callApi(getLayoutAisle(aisleName, warehouseId, highlightBrand)).catch(() => {})
    }
  }, [open, aisleName, warehouseId, highlightBrand])

  const aisleData = state.data as AisleResponse | undefined

  const sortedLevels = useMemo(
    () => (aisleData ? aisleData.levels.filter(isAxisValue).sort(numSort).reverse() : []),
    [aisleData]
  )
  const sortedColumns = useMemo(
    () => (aisleData ? aisleData.columns.filter(isAxisValue).sort(numSort) : []),
    [aisleData]
  )

  const cellMap = useMemo(() => {
    const map = new Map<string, AisleCell>()
    if (aisleData) aisleData.cells.forEach((c) => map.set(`${c.level}-${c.column}`, c))
    return map
  }, [aisleData])

  /** Click simple: muestra el resumen de la celda. */
  const handleCellClick = useCallback(
    (cellKey: string, anchorEl: HTMLElement) => setOpen({ key: cellKey, anchorEl }),
    [setOpen]
  )

  /**
   * Doble click: abre el detalle. El primer click del gesto ya abrió el
   * resumen, así que se cierra antes de montar el modal.
   */
  const handleCellDoubleClick = useCallback(
    (cellKey: string) => {
      const cell = cellMap.get(cellKey)
      if (!cell?.isConfigured) return

      close()
      setProductsCell({
        aisle: aisleName,
        column: cell.column,
        level: cell.level,
        totalStock: cell.totalStock,
        maxCapacity: cell.maxCapacity,
        occupancyPct: cell.occupancyPct,
      })
    },
    [cellMap, aisleName, close]
  )

  const cellSize = aisleData
    ? Math.max(36, Math.min(48, Math.floor(1200 / Math.max(sortedColumns.length, 1))))
    : 42

  return (
    <Fragment>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='xl'
        fullWidth
        slotProps={{ paper: { sx: styles.paper } }}
      >
        <DialogTitle sx={styles.header}>
          <Typography fontWeight='bold' sx={styles.headerTitle}>
            Vista Frontal: Pasillo {aisleName}
          </Typography>
          <IconButton onClick={onClose} size='small' sx={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box sx={styles.controlsBar}>
          <Typography
            variant='caption'
            color='text.secondary'
            fontWeight={600}
            sx={styles.modeLabel}
          >
            Modo
          </Typography>
          {(Object.keys(MAP_MODE_LABELS) as ViewMode[]).map((m) => (
            <Button
              key={m}
              size='small'
              variant={mode === m ? 'contained' : 'outlined'}
              onClick={() => onModeChange(m)}
              sx={styles.modeButton(mode === m)}
            >
              {MAP_MODE_LABELS[m]}
            </Button>
          ))}
        </Box>

        {/* Leyenda */}
        <Box sx={styles.legendBar}>
          <MapLegend mode={mode} brandColors={brandColors} zoneColors={zoneColors} />
        </Box>

        <DialogContent sx={styles.content}>
          {state.status === Status.Loading && (
            <Box sx={styles.loadingWrapper}>
              <CircularProgress />
            </Box>
          )}
          {state.status === Status.Error && (
            <Typography color='error' sx={styles.errorText}>
              {state.error ?? 'Error al cargar el pasillo'}
            </Typography>
          )}

          {aisleData && (
            <Box sx={styles.body}>
              {/* NIVEL — etiqueta vertical fija, fuera del área scrollable */}
              <Box sx={axisLabelSx}>
                NIVEL
              </Box>

              {/* Columna derecha: COLUMNA label fijo + área scrollable */}
              <Box sx={gridStyles.axisColumn}>
                {/* COLUMNA label fuera del scroll: no se desplaza con el scroll horizontal */}
                <Typography
                  variant='caption'
                  fontWeight='bold'
                  color='text.secondary'
                  sx={gridStyles.columnAxisLabel(LEVEL_CHIP_W, 0.75)}
                >
                  COLUMNA
                </Typography>

                <Box ref={containerRef} sx={styles.scrollArea}>
                  <Box
                    sx={styles.grid(
                      `${LEVEL_CHIP_W}px repeat(${sortedColumns.length}, ${cellSize}px)`
                    )}
                  >
                    <Box sx={gridStyles.cornerCell} />

                    {sortedColumns.map((col) => (
                      <Box key={col} sx={gridStyles.columnHeader}>
                        <Typography variant='caption' fontWeight={600} fontSize='0.65rem' color={GRID_COL_HEADER_TEXT} sx={styles.headerCellText}>
                          {col}
                        </Typography>
                      </Box>
                    ))}

                    {/* Filas de niveles */}
                    {sortedLevels.map((level) => (
                      <Fragment key={level}>
                        {/* Chip de nivel */}
                        <Box sx={styles.levelChip(cellSize)}>
                          <Typography variant='caption' fontWeight={600} fontSize='0.7rem' color={GRID_COL_HEADER_TEXT} sx={styles.levelChipText}>
                            {level}
                          </Typography>
                        </Box>

                        {/* Celdas de datos */}
                        {sortedColumns.map((col) => {
                          const cell = cellMap.get(`${level}-${col}`)
                          if (!cell) {
                            return <Box key={col} sx={gridStyles.unconfiguredCell(cellSize)} />
                          }
                          const cellKey = `${level}-${col}`
                          return (
                            <AisleMapCell
                              key={col}
                              cell={cell}
                              size={cellSize}
                              brandColors={brandColors}
                              zoneColors={zoneColors}
                              mode={mode}
                              highlightBrand={highlightBrand}
                              cellKey={cellKey}
                              isSummaryOpen={openCell?.key === cellKey}
                              onClick={handleCellClick}
                              onDoubleClick={handleCellDoubleClick}
                            />
                          )
                        })}
                      </Fragment>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Uno solo para toda la vista de pasillo, anclado a la celda seleccionada. */}
      <CellSummaryPopper
        openCell={openCell}
        cell={openCell ? cellMap.get(openCell.key) : undefined}
        rowLabel='Nivel'
        brandColors={brandColors}
        zoneColors={zoneColors}
        showDetailHint={openCell ? Boolean(cellMap.get(openCell.key)?.isConfigured) : false}
      />

      {productsCell && (
        <ProductsModal
          open={Boolean(productsCell)}
          warehouseId={warehouseId}
          cell={productsCell}
          brandColors={brandColors}
          onClose={() => setProductsCell(null)}
        />
      )}
    </Fragment>
  )
}
